'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

// Registro de deduplicación de pagos en cliente para prevenir doble toast por eventos simultáneos
const pagosToastsRecientes = new Map<string, number>();

function esPagoToastDuplicado(key: string): boolean {
  if (!key) return false;
  const ahora = Date.now();
  for (const [k, ts] of pagosToastsRecientes.entries()) {
    if (ahora - ts > 15000) pagosToastsRecientes.delete(k);
  }
  if (pagosToastsRecientes.has(key)) return true;
  pagosToastsRecientes.set(key, ahora);
  return false;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  metadata?: any;
}

export interface ServerErrorDetail {
  where: string;
  info: string;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>, onlyToast?: boolean) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  unreadCount: number;
  toasts: Notification[];
  removeToast: (id: string) => void;
  serverError: ServerErrorDetail | null;
  setServerError: (error: ServerErrorDetail | null) => void;
  socket: any | null;
  accessToken: string | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts, setToasts] = useState<Notification[]>([]);
  const [serverError, setServerError] = useState<ServerErrorDetail | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [socket, setSocket] = useState<any | null>(null);

  // Cargar desde localStorage al inicio
  useEffect(() => {
    const saved = localStorage.getItem('ispadmin_notifications');
    let loadedNotifications: Notification[] = [];
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Filtrar notificaciones simuladas antiguas (mocks)
        loadedNotifications = parsed
          .filter((n: any) => n && n.id && !String(n.id).startsWith('mock-'))
          .map((n: any) => ({ ...n, timestamp: new Date(n.timestamp) }));
        
        // Limpiar el almacenamiento si contenía mocks
        if (loadedNotifications.length !== parsed.length) {
          localStorage.setItem('ispadmin_notifications', JSON.stringify(loadedNotifications));
        }
      } catch (e) {
        console.error('Error parsing notifications:', e);
      }
    }

    setNotifications(loadedNotifications);
  }, []);

  // Guardar en localStorage cuando cambien
  useEffect(() => {
    localStorage.setItem('ispadmin_notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Interceptar errores de red/servidor (fetch global)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const originalFetch = window.fetch;
    window.fetch = async function (input, init) {
      try {
        const response = await originalFetch(input, init);
        if (!response.ok && response.status >= 500) {
          let info = `Código de estado: ${response.status} ${response.statusText}`;
          let where = 'General (Servidor)';

          const urlStr = typeof input === 'string' 
            ? input 
            : (input instanceof Request ? input.url : '');

          if (urlStr) {
            try {
              const urlObj = new URL(urlStr, window.location.origin);
              const path = urlObj.pathname;
              if (path.includes('/api/proyectos')) {
                where = 'Módulo: Proyectos';
              } else if (path.includes('/api/configuracion/general/integraciones') || path.includes('/api/integraciones')) {
                where = 'Integración';
              } else if (path.includes('/api/contratos')) {
                where = 'Módulo: Contratos';
              } else if (path.includes('/api/soporte')) {
                where = 'Módulo: Soporte';
              } else if (path.includes('/api/clientes')) {
                where = 'Módulo: Clientes';
              } else if (path.includes('/api/facturas')) {
                where = 'Módulo: Facturas';
              } else if (path.includes('/api/pagos')) {
                where = 'Módulo: Pagos';
              } else if (path.includes('/api/comunicaciones')) {
                where = 'Módulo: Comunicaciones';
              } else if (path.includes('/api/configuracion')) {
                where = 'Módulo: Configuración';
              } else {
                where = `API: ${path}`;
              }
            } catch (_) {}
          }

          try {
            const clone = response.clone();
            const data = await clone.json();
            
            if (data && data.integration) {
              where = `Integración: ${data.integration}`;
            } else if (
              (data && data.error && (data.error.includes('invalid_grant') || data.error.includes('Token has been expired or revoked'))) ||
              (data && data.detail && (data.detail.includes('invalid_grant') || data.detail.includes('Token has been expired or revoked')))
            ) {
              where = 'Integración: Drive';
            } else if (urlStr && (urlStr.includes('/upload-doc') || urlStr.includes('/drive'))) {
              where = 'Integración: Drive';
            }

            if (data && data.detail) {
              info = data.detail;
            } else if (data && data.error) {
              if (data.error.includes('invalid_grant') || data.error.includes('Token has been expired or revoked')) {
                info = 'Token has been expired or revoked';
              } else {
                info = data.error;
              }
            } else if (data && data.message) {
              info = data.message;
            }
          } catch (_) {
            try {
              const cloneText = response.clone();
              const text = await cloneText.text();
              if (text && text.length < 500) {
                if (text.includes('invalid_grant') || text.includes('Token has been expired or revoked')) {
                  info = 'Token has been expired or revoked';
                  where = 'Integración: Drive';
                } else {
                  info = text;
                }
              }
            } catch (__) {}
          }

          console.error(`[FetchError] Error de API (status >= 500) detectado en frontend:`, { donde: where, info });
          setServerError({ where, info });
        }
        return response;
      } catch (err: any) {
        // Ignorar cancelaciones intencionales de AbortController para no disparar el modal de error ni re-renders globales
        if (err?.name === 'AbortError' || String(err?.message || '').toLowerCase().includes('aborted')) {
          throw err;
        }

        // Errores de conexión de red (Failed to fetch)
        let where = 'Conexión de red / Servidor inalcanzable';
        let info = err.message || String(err);

        const urlStr = typeof input === 'string' 
          ? input 
          : (input instanceof Request ? input.url : '');

        if (
          urlStr &&
          (urlStr.includes('8400') ||
            urlStr.includes('localhost:8400') ||
            urlStr.includes('127.0.0.1:8400') ||
            urlStr.includes('/socket.io'))
        ) {
          // No reportar fallos del servidor de diseño local ni reconexiones de Socket.IO
          throw err;
        }

        if (urlStr) {
          try {
            const urlObj = new URL(urlStr, window.location.origin);
            where = `API: ${urlObj.pathname}`;
          } catch (_) {}
        }

        console.error(`[FetchError] Error de conexión de red detectado en frontend:`, { donde: where, info });
        setServerError({ where, info });
        throw err;
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addNotification = useCallback((n: Omit<Notification, 'id' | 'timestamp' | 'read'>, onlyToast = false) => {
    const newNotification: Notification = {
      ...n,
      id: Math.random().toString(36).substring(2, 9),
      timestamp: new Date(),
      read: false,
    };
    
    if (!onlyToast) {
      setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Limitar a 50
    }
    
    // Añadir como toast evitando duplicados de pago activos en pantalla con el mismo estado
    setToasts(prev => {
      if (newNotification.metadata?.isPaymentWebhook) {
        const ref = newNotification.metadata?.paymentData?.referencia;
        const pid = newNotification.metadata?.paymentData?.pagoId;
        const est = newNotification.metadata?.paymentData?.estado;
        const yaExiste = prev.some(t =>
          t.metadata?.isPaymentWebhook && (
            ((ref && t.metadata?.paymentData?.referencia === ref) ||
             (pid && t.metadata?.paymentData?.pagoId === pid)) &&
            t.metadata?.paymentData?.estado === est
          )
        );
        if (yaExiste) return prev;
      }
      return [...prev, newNotification];
    });
    
    // Auto remover toast después de 5 segundos (5000ms) solo si no es persistente
    if (!newNotification.metadata?.persistent) {
      setTimeout(() => {
        removeToast(newNotification.id);
      }, 5000);
    }
  }, [removeToast]);

  const [currentUser, setCurrentUser] = useState<any>(null);

  // Obtener Sesión del Operador
  useEffect(() => {
    fetch('/api/auth/session')
      .then(async (r) => {
        if (!r.ok) return null;
        const contentType = r.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) return null;
        return r.json();
      })
      .then(d => {
        if (d && d.accessToken) {
          setAccessToken(d.accessToken);
        }
        if (d && d.user) {
          setCurrentUser(d.user);
        }
      })
      .catch(err => console.warn('Aviso al obtener sesión para notificaciones:', err));
  }, []);

  // Conectar a Socket.io en el Microservicio de Chat
  useEffect(() => {
    if (!accessToken || !currentUser) return;

    console.log('🔌 Conectando socket global de notificaciones...');
    const s = io({
      path: '/socket.io',
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 8,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 15000,
      timeout: 10000,
    });

    s.on('connect', () => {
      console.log('💚 Socket global conectado con éxito!');
    });

    s.on('connect_error', (err) => {
      if (err?.message === 'TOKEN_EXPIRED' || err?.message?.includes('Token') || err?.message?.includes('Autenticación')) {
        console.warn('⚠️ [Global Socket] Autenticación fallida o token expirado. Deteniendo reintentos.');
        s.disconnect();
      }
    });

    s.on('new_message_update', (data: { conversation: any; message: any }) => {
      console.log('📬 [Global Socket] Mensaje recibido:', data);

      // Solo mostrar toast si el mensaje es entrante (inbound)
      // Y si NO estamos en la página del chat
      if (data.message && data.message.direction === 'inbound') {
        const isChatPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/apps/chat');
        if (!isChatPage) {
          const status = data.conversation?.status; // 'PENDIENTE' o 'EN_ATENCION'
          const userId = currentUser?.id;

          let shouldNotify = false;
          const isPersistent = false;

          // Solo mostrar notificación toaster si la conversación está en atención ('EN_ATENCION')
          // y únicamente al operador que la está atendiendo
          if (status === 'EN_ATENCION') {
            shouldNotify = Boolean(
              data.conversation?.assigned_agent_id &&
              userId &&
              String(data.conversation.assigned_agent_id) === String(userId)
            );
          }

          if (shouldNotify) {
            addNotification({
              title: `Nuevo mensaje de ${data.conversation.client_name || data.conversation.client_phone}`,
              message: data.message.body || 'Archivo multimedia adjunto',
              type: 'info',
              metadata: {
                conversationId: data.conversation._id,
                clientPhone: data.conversation.client_phone,
                persistent: isPersistent
              }
            }, true);
          }
        }
      }
    });

    // Escuchar cuando un chat es reclamado por un agente
    s.on('chat_assigned', (data: { conversationId: string; agentId: string; agentName: string }) => {
      console.log('📬 [Global Socket] Chat asignado:', data);
      // Quitar de la pantalla el toast flotante de esa conversación
      setToasts(prev => prev.filter(t => t.metadata?.conversationId !== data.conversationId));
    });

    // Escuchar cuando un chat es removido (cerrado o transferido)
    s.on('chat_removed', (data: { conversationId: string; reason: string }) => {
      console.log('📬 [Global Socket] Chat removido:', data);
      setToasts(prev => prev.filter(t => t.metadata?.conversationId !== data.conversationId));
    });

    // Escuchar solicitudes de atención recibidas por parte de otros operadores
    s.on('claim_request_received', (data: {
      requestId: string;
      conversationId: string;
      clientName: string;
      clientPhone: string;
      requesterAgentId: string;
      requesterAgentName: string;
      timestamp: string;
    }) => {
      console.log('📬 [Global Socket] Solicitud de atención recibida:', data);
      addNotification({
        title: 'Solicitud de Transferencia de Chat',
        message: `El operador ${data.requesterAgentName} solicita tomar la atención del cliente ${data.clientName || data.clientPhone}.`,
        type: 'warning',
        metadata: {
          isClaimRequest: true,
          requestId: data.requestId,
          conversationId: data.conversationId,
          requesterAgentId: data.requesterAgentId,
          requesterAgentName: data.requesterAgentName,
          clientName: data.clientName,
          clientPhone: data.clientPhone,
          persistent: true
        }
      }, true);
    });

    // Escuchar respuesta de aprobación/rechazo emitida por el operador asignado
    s.on('claim_request_response', (data: {
      action: 'APPROVE' | 'REJECT';
      conversationId: string;
      responderAgentName: string;
      conversation?: any;
    }) => {
      console.log('📬 [Global Socket] Respuesta de solicitud de atención:', data);
      if (data.action === 'APPROVE') {
        addNotification({
          title: '¡Solicitud Aprobada!',
          message: `El operador ${data.responderAgentName} aprobó tu solicitud. La conversación ha sido asignada a tu bandeja.`,
          type: 'success',
          metadata: {
            conversationId: data.conversationId
          }
        }, true);
      } else {
        addNotification({
          title: 'Solicitud Rechazada',
          message: `El operador ${data.responderAgentName} rechazó tu solicitud para atender esta conversación.`,
          type: 'error',
          metadata: {
            conversationId: data.conversationId
          }
        }, true);
      }
    });

    // Escuchar eventos de Webhooks de Pasarelas de Pago (Bold, Wompi, Bancolombia RIN, etc.)
    s.on('payment_webhook_received', (data: any) => {
      console.log('💳 [Global Socket] Pago webhook recibido:', data);

      // Deduplicación en cliente (evita duplicidad por concurrencia o retries)
      const estadoPago = String(data.estado || 'APROBADO').toUpperCase();
      const esRechazado = ['RECHAZADO', 'DECLINADO', 'FALLIDO', 'FAILED', 'DECLINED'].includes(estadoPago);
      const dedupeKey = `${estadoPago}_` + String(data.pagoId || data.referencia || `${data.pasarela}_${data.monto}_${data.cliente?.id || ''}`).trim();
      if (esPagoToastDuplicado(dedupeKey)) {
        console.log('🛡️ [Global Socket] Toast de pago duplicado descartado en cliente:', dedupeKey);
        return;
      }

      const userId = currentUser?.id ? String(currentUser.id) : null;
      const userRol = String(currentUser?.rol || '').toUpperCase().trim();
      const isAdmin = ['ADMIN', 'ADMINISTRADOR', 'SUPER_ADMIN', 'SUPERADMIN'].includes(userRol);

      const savedActiveSedes = typeof window !== 'undefined' ? localStorage.getItem('chat_operator_active_sedes') : null;
      const userSedes = (savedActiveSedes 
        ? JSON.parse(savedActiveSedes) 
        : (currentUser?.sedes_ids || [])).map(String);

      const paymentSedeId = data.cliente?.sede_id ? String(data.cliente.sede_id) : null;
      const assignedUserId = data.cliente?.usuario_asignado_id 
        ? String(data.cliente.usuario_asignado_id)
        : (data.solicitud?.solicitado_por_usuario_id ? String(data.solicitud.solicitado_por_usuario_id) : null);

      // Verificación de destino:
      // 1. ¿Es el operador asignado a este cliente o solicitud?
      const isAssignedUser = Boolean(assignedUserId && userId && assignedUserId === userId);

      // 2. ¿El operador pertenece a la misma sede del cliente que realizó el pago?
      const isSameSede = Boolean(paymentSedeId && userSedes.includes(paymentSedeId));

      // 3. ¿El usuario es administrador? (Supervisa todas las sedes o la sede actual)
      const shouldNotify = isAssignedUser || isSameSede || (isAdmin && (!paymentSedeId || userSedes.length === 0 || userSedes.includes(paymentSedeId)));

      if (shouldNotify) {
        // Notificar a componentes activos (como SolicitarPagoModal) vía CustomEvent
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ispadmin:pago_recibido', { detail: data }));
        }

        const montoFormateado = Number(data.monto || 0).toLocaleString('es-CO');

        addNotification({
          title: esRechazado 
            ? `Pago Declinado (${data.pasarela || 'Pasarela'})` 
            : (estadoPago === 'PENDIENTE_ASIGNACION'
              ? `Pago por Conciliar (${data.pasarela || 'Online'})`
              : `Pago Acreditado (${data.pasarela || 'Online'})`),
          message: `${data.cliente?.nombre || 'Cliente'}: $${montoFormateado} COP${data.motivoRechazo ? ` • ${data.motivoRechazo}` : ''}`,
          type: esRechazado ? 'error' : (estadoPago === 'PENDIENTE_ASIGNACION' ? 'warning' : 'success'),
          metadata: {
            isPaymentWebhook: true,
            paymentData: data,
            isAssignedToCurrentUser: isAssignedUser,
            persistent: false,
          }
        }, true);
      }
    });

    setSocket(s);

    return () => {
      s.disconnect();
    };
  }, [accessToken, currentUser, addNotification]);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      markAsRead,
      markAllAsRead,
      removeNotification,
      unreadCount,
      toasts,
      removeToast,
      serverError,
      setServerError,
      socket,
      accessToken
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
